import { useState, useMemo, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import Bowser from 'bowser'
import { useAuth } from '../hooks/useAuth'
import { AppIcon } from './ui/icon'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { tokensApi } from '../lib/api/tokens'
import { useGeoIPBulk, formatLocation, formatProvider, canonicalizeIP } from '../api/geoip'
import { apiClient } from '../api/client'
import { Button } from './ui/button'
import { Card, CardContent } from './ui/card'
import { Badge } from './ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from './ui/dialog'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog'
import { useTranslation } from 'react-i18next'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { EmptyState } from './ui/empty-state'
import { mdiCalendar, mdiCellphone, mdiCheck, mdiClockOutline, mdiContentCopy, mdiIpNetworkOutline, mdiLan, mdiLaptop, mdiMapMarker, mdiMonitor, mdiRefresh, mdiTablet, mdiWeb } from '@mdi/js'

// Define UserToken interface based on our schema
interface UserToken {
  uid: string
  type: string
  properties?: {
    device_type?: string
    browser?: string
    ip_address?: string
    location?: string
    created_with?: {
      user_agent?: string
      remote_addr?: string
      // The second factor that verified the session (spec 2026-10-01-03).
      mfa?: { factor?: string; at?: string }
    }
    device_info?: {
      device_type?: string
      os?: string
      os_version?: string
      browser?: string
      version?: string
      is_mobile?: boolean
      is_tablet?: boolean
      is_desktop?: boolean
    }
  }
  created_at: string
  last_used_at?: string | null
  expires_at: string
  // True for the refresh-token row that minted the access token used in this
  // request. Set server-side; absent when the caller is on a PAT/email/SSH.
  is_current?: boolean
}

interface UserTokensPageProps {
  orgCode: string
  userUid?: string
  tokenType?: 'refresh_token' | 'pat' | 'email'
  /**
   * `member`: an org admin looking at a member's sessions in this
   * organization (members endpoints, "log out everywhere" for that member).
   * Default `self`: the caller's own sessions.
   */
  mode?: 'self' | 'member'
  /** Who the sessions belong to, shown in member mode. */
  memberLabel?: string
  /** Last breadcrumb, "Sessions" by default (the Security tab names itself). */
  breadcrumbTitle?: string
}

// SessionNetwork shows where a session comes from: location, IP and network
// operator side by side, so an admin can tell a colleague's laptop from a
// stranger's. Each line is hidden when it has no value.
function SessionNetwork({ ip, location, provider }: { ip?: string; location: string | null; provider: string | null }) {
  const { t } = useTranslation('users')
  const [copied, setCopied] = useState(false)
  if (!ip && !location && !provider) return null

  const copy = async () => {
    if (!ip) return
    try {
      await navigator.clipboard.writeText(ip)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // The IP is on screen and selectable: a refused clipboard is no error.
    }
  }

  return (
    <div
      className="flex flex-col gap-1 text-sm text-muted-foreground self-start sm:items-end sm:flex-shrink-0 min-w-0"
      data-testid="session-network"
    >
      {location && (
        <div className="flex items-center gap-1 min-w-0" data-testid="session-location">
          <AppIcon path={mdiMapMarker} size="sm" className="flex-shrink-0" />
          <span className="break-words">{location}</span>
        </div>
      )}
      {ip && (
        <div className="flex items-center gap-1 min-w-0" data-testid="session-ip">
          <AppIcon path={mdiIpNetworkOutline} size="sm" className="flex-shrink-0" />
          <span className="font-mono text-xs text-foreground break-all">{ip}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={copy}
            aria-label={t('sessions.copy_ip')}
            title={copied ? t('sessions.ip_copied') : t('sessions.copy_ip')}
            data-testid="session-ip-copy"
          >
            <AppIcon path={copied ? mdiCheck : mdiContentCopy} size="sm" />
          </Button>
        </div>
      )}
      {provider && (
        <div className="flex items-center gap-1 min-w-0" data-testid="session-provider" title={t('sessions.provider_hint')}>
          <AppIcon path={mdiLan} size="sm" className="flex-shrink-0" />
          <span className="break-words">{provider}</span>
        </div>
      )}
    </div>
  )
}

async function fetchMemberSessions(orgCode: string, userUid: string) {
  const { data, error } = await apiClient.GET('/api/v1/organizations/{orgCode}/members/{userUid}/sessions', {
    params: { path: { orgCode, userUid } },
  })
  if (error) {
    throw new Error('Failed to fetch member sessions')
  }
  return data
}

export function UserTokensPage({ orgCode, userUid, tokenType, mode = 'self', memberLabel, breadcrumbTitle }: UserTokensPageProps) {
  const { user: authUser, refreshAccessToken } = useAuth()
  const queryClient = useQueryClient()
  const { t } = useTranslation('users')
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const isMemberMode = mode === 'member' && !!userUid

  const handleRefreshToken = useCallback(async () => {
    setIsRefreshing(true)
    try {
      await refreshAccessToken()
      queryClient.invalidateQueries({ queryKey: ['userTokens'] })
    } catch (error) {
      console.error('Failed to refresh token:', error)
    } finally {
      setIsRefreshing(false)
    }
  }, [refreshAccessToken, queryClient])

  // Use provided userUid or fall back to the caller's OWN sessions. `me` is
  // what addresses the /users/me group, which needs no `contacts` permission
  // (spec 2026-09-06-04) — the uid stays in the query keys.
  const targetUserUid = userUid || 'me'
  const isOwnProfile = !isMemberMode && (targetUserUid === 'me' || targetUserUid === authUser?.uid)
  // `me` is a URL segment, not an identity: the query key carries the caller's
  // real uid so the own-sessions cache never collides with — and invalidates
  // alongside — the `/users/{uid}` variant a manager opens on the same person.
  const queryKeyUserUid = userUid || authUser?.uid || 'me'

  // Set breadcrumbs
  const breadcrumbs = useMemo(() => [
    { title: t('breadcrumb.home'), path: `/${orgCode}` },
    ...(isMemberMode
      ? [
          { title: t('breadcrumb.settings'), path: `/${orgCode}/settings` },
          { title: t('breadcrumb.members'), path: `/${orgCode}/settings/members` },
          ...(memberLabel ? [{ title: memberLabel }] : []),
        ]
      : isOwnProfile
        ? [{ title: t('breadcrumb.user'), path: `/${orgCode}/user/me/profile` }]
        : [{ title: t('breadcrumb.contacts'), path: `/${orgCode}/users` }, { title: t('breadcrumb.user'), path: `/${orgCode}/users/${targetUserUid}` }]
    ),
    { title: breadcrumbTitle ?? t('breadcrumb.sessions') }
  ], [orgCode, isOwnProfile, isMemberMode, memberLabel, targetUserUid, breadcrumbTitle, t])

  useBreadcrumbs(breadcrumbs)

  // Query for user tokens
  const { data: tokensData, isLoading, error } = useQuery({
    queryKey: isMemberMode
      ? ['memberSessions', orgCode, userUid]
      : ['userTokens', orgCode, queryKeyUserUid, tokenType],
    queryFn: () => isMemberMode
      ? fetchMemberSessions(orgCode, userUid!)
      : tokensApi.getUserTokens(orgCode, targetUserUid, tokenType || 'refresh_token'),
    enabled: !!targetUserUid,
    refetchInterval: 30000, // Refresh every 30 seconds
  })

  // "Log out everywhere" for a member, in member mode: ends every session
  // they hold in this organization.
  const logoutMemberMutation = useMutation({
    mutationFn: async () => {
      const { error } = await apiClient.DELETE('/api/v1/organizations/{orgCode}/members/{userUid}/sessions', {
        params: { path: { orgCode, userUid: userUid! } },
      })
      if (error) throw new Error('Failed to log the member out')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memberSessions', orgCode, userUid] })
      queryClient.invalidateQueries({ queryKey: ['members', orgCode] })
    },
  })

  // Resolve IPs to a location and a network operator via the bulk geoip
  // endpoint. The IP shows at once; location and provider join it when this
  // resolves.
  const sessionIPs = useMemo(() => {
    const set = new Set<string>()
    for (const tok of tokensData?.tokens ?? []) {
      const ip = (tok as UserToken).properties?.created_with?.remote_addr
      if (ip) set.add(ip)
    }
    return Array.from(set)
  }, [tokensData])
  const { data: geoipMap } = useGeoIPBulk(sessionIPs)

  // Sort: current session pinned to the top, then by last_used_at desc.
  // The server returns rows in insertion order, which would let the active
  // row jump around as new sessions arrive — pinning here keeps it stable.
  const sortedTokens = useMemo<UserToken[]>(() => {
    const list = [...((tokensData?.tokens ?? []) as UserToken[])]
    list.sort((a, b) => {
      if (!!a.is_current !== !!b.is_current) return a.is_current ? -1 : 1
      const ta = a.last_used_at ? new Date(a.last_used_at).getTime() : 0
      const tb = b.last_used_at ? new Date(b.last_used_at).getTime() : 0
      return tb - ta
    })
    return list
  }, [tokensData])

  const otherSessionsCount = useMemo(
    () => sortedTokens.filter((t) => !t.is_current).length,
    [sortedTokens],
  )

  // Kill-other-sessions mutation. Unlike the legacy "Disconnect from
  // everywhere" path, this keeps the current device's session alive, so
  // there is no redirect to /login on success.
  const killOthersMutation = useMutation({
    mutationFn: () => tokensApi.logoutOthers(orgCode),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userTokens'] })
      setIsLogoutDialogOpen(false)
    },
    onError: (error: Error) => {
      console.error('Failed to sign out other sessions:', error)
    },
  })

  const getDeviceIcon = (deviceInfo: { device_type?: string; os?: string; os_version?: string; browser?: string; version?: string; is_mobile?: boolean; is_tablet?: boolean; is_desktop?: boolean; } | undefined, userAgent: string | undefined) => {
    // Use device_info from backend if available, otherwise fall back to parsing user agent
    const isTablet = deviceInfo?.is_tablet || userAgent?.toLowerCase().includes('tablet')
    const isMobile = deviceInfo?.is_mobile || userAgent?.toLowerCase().includes('mobile')

    if (isMobile) {
      return <AppIcon path={mdiCellphone} size="md" className="text-primary" />
    } else if (isTablet) {
      return <AppIcon path={mdiTablet} size="md" className="text-green-600 dark:text-green-400" />
    } else {
      return <AppIcon path={mdiMonitor} size="md" />
    }
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return t('sessions.never')
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(dateString))
  }

  class DeviceInfo {
    device_type?: 'mobile' | 'tablet' | 'desktop' | 'unknown'
    os?: string
    os_version?: string
    browser?: string
    browser_version?: string
    is_mobile?: boolean
    is_tablet?: boolean
    is_desktop?: boolean
  }

  function getDeviceInfo(userAgent: string | undefined): DeviceInfo {
    const deviceInfo = new DeviceInfo()

    if (!userAgent) {
      deviceInfo.device_type = 'unknown'
      deviceInfo.os = 'unknown'
      deviceInfo.browser = 'unknown'
      deviceInfo.is_mobile = false
      deviceInfo.is_tablet = false
      deviceInfo.is_desktop = false
      return deviceInfo
    }

    try {
      const browser = Bowser.getParser(userAgent)
      const result = browser.getResult()

      // Device type detection
      if (result.platform.type === 'mobile') {
        deviceInfo.device_type = 'mobile'
        deviceInfo.is_mobile = true
        deviceInfo.is_tablet = false
        deviceInfo.is_desktop = false
      } else if (result.platform.type === 'tablet') {
        deviceInfo.device_type = 'tablet'
        deviceInfo.is_mobile = false
        deviceInfo.is_tablet = true
        deviceInfo.is_desktop = false
      } else {
        deviceInfo.device_type = 'desktop'
        deviceInfo.is_mobile = false
        deviceInfo.is_tablet = false
        deviceInfo.is_desktop = true
      }

      // OS information
      deviceInfo.os = result.os.name || 'unknown'
      if (deviceInfo.os == 'macOS') {
        deviceInfo.os_version = result.os.version || undefined
      }

      // Browser information
      deviceInfo.browser = result.browser.name || 'unknown'
      deviceInfo.browser_version = result.browser.version || undefined

    } catch {
      // Fallback for parsing errors
      deviceInfo.device_type = 'unknown'
      deviceInfo.os = 'unknown'
      deviceInfo.browser = 'unknown'
      deviceInfo.is_mobile = userAgent.toLowerCase().includes('mobile')
      deviceInfo.is_tablet = userAgent.toLowerCase().includes('tablet')
      deviceInfo.is_desktop = !deviceInfo.is_mobile && !deviceInfo.is_tablet
    }

    return deviceInfo
  }


  const getTokenTypeDisplay = (type: string) => {
    switch (type) {
      case 'refresh_token':
        return { label: 'Session', variant: 'default' as const }
      case 'access_token':
        return { label: 'Access Token', variant: 'secondary' as const }
      case 'pat':
        return { label: 'Personal Access Token', variant: 'outline' as const }
      case 'email':
        return { label: 'Email Token', variant: 'outline' as const }
      default:
        return { label: type, variant: 'secondary' as const }
    }
  }

  if (!authUser) {
    return (
      <div className="bg-card rounded-lg shadow-lg p-6">
        <div className="text-center">
          <div className="text-destructive mb-4">{t('sessions.auth_required')}</div>
          <Link to="/$org/login" params={{ org: orgCode }} search={{ next: undefined, email: undefined, password: undefined }}>
            <Button>{t('sessions.go_to_login')}</Button>
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-card rounded-lg shadow-lg">
        <div className="px-4 sm:px-6 py-4 border-b border-border">
          <PageHeader
              icon={SECTION_ICONS.tokens}
              title={t('sessions.title')}
              description={isMemberMode
                ? t('sessions.member_subtitle', { name: memberLabel || '' })
                : t('sessions.subtitle')}
              actions={
              isMemberMode ? (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      disabled={(tokensData?.total_count ?? 0) === 0 || logoutMemberMutation.isPending}
                      data-testid="member-logout-everywhere-btn"
                    >
                      {t('sessions.member_logout_button')}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('sessions.member_logout_title')}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('sessions.member_logout_description', { count: tokensData?.total_count ?? 0, name: memberLabel || '' })}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="member-logout-everywhere-cancel">{t('common:actions.cancel')}</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => logoutMemberMutation.mutate()}
                        data-testid="member-logout-everywhere-confirm"
                      >
                        {t('sessions.member_logout_confirm')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              ) : (
              <>
            <div className="flex flex-wrap gap-2">
              {isOwnProfile && (
                <Button
                  variant="outline"
                  onClick={handleRefreshToken}
                  disabled={isRefreshing}
                  data-testid="refresh-token-btn"
                >
                  <AppIcon path={mdiRefresh} size="sm" className={`mr-2 ${isRefreshing ? 'motion-safe:animate-spin' : ''}`} />
                  {isRefreshing ? t('sessions.refreshing') : t('sessions.refresh_token')}
                </Button>
              )}
              <Dialog open={isLogoutDialogOpen} onOpenChange={setIsLogoutDialogOpen}>
                <DialogTrigger asChild>
                  <Button
                    variant="destructive"
                    disabled={otherSessionsCount === 0}
                    data-testid="kill-other-sessions-btn"
                  >
                    {t('sessions.kill_others_button')}
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>{t('sessions.kill_others_title')}</DialogTitle>
                    <DialogDescription>
                      {otherSessionsCount === 0
                        ? t('sessions.kill_others_description_zero')
                        : t('sessions.kill_others_description', { count: otherSessionsCount })}
                    </DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setIsLogoutDialogOpen(false)}
                      disabled={killOthersMutation.isPending}
                    >
                      {t('common:actions.cancel')}
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() => killOthersMutation.mutate()}
                      disabled={killOthersMutation.isPending || otherSessionsCount === 0}
                      data-testid="kill-other-sessions-confirm-btn"
                    >
                      {killOthersMutation.isPending ? t('sessions.kill_others_in_progress') : t('sessions.kill_others_confirm')}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
              </>
              )}
          />
        </div>

        <div className="p-4 sm:p-6">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="motion-safe:animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3"></div>
              <span className="text-muted-foreground">{t('sessions.loading')}</span>
            </div>
          )}

          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4 mb-6">
              <div className="text-destructive text-sm">
                {t('sessions.error_loading', { error: (error as any)?.title || (error as any)?.message || '' })}
              </div>
            </div>
          )}

          {tokensData && (
            <>
              <div className="mb-6">
                <div className="text-sm text-muted-foreground mb-4" data-testid="sessions-count">
                  {isMemberMode
                    ? t('sessions.member_count', { count: tokensData.total_count })
                    : t('sessions.count', { count: tokensData.total_count })}
                </div>
              </div>

              {sortedTokens.length === 0 ? (
                <EmptyState
                  variant="inline"
                  icon={mdiWeb}
                  title={t('sessions.no_sessions_title')}
                  description={t('sessions.no_sessions_description')}
                />
              ) : (
                <div className="grid gap-4">
                  {sortedTokens.map((token: UserToken) => {
                    const tokenTypeInfo = getTokenTypeDisplay(token.type)
                    const createdWith = token.properties?.created_with
                    const userAgent = createdWith?.user_agent
                    const ipAddress = createdWith?.remote_addr
                    const deviceInfo = getDeviceInfo(createdWith?.user_agent)
                    const ip = ipAddress ? canonicalizeIP(ipAddress) : undefined
                    const geo = ip ? geoipMap?.[ip] : undefined
                    const location = formatLocation(geo)
                    const provider = formatProvider(geo)

                    return (
                      <Card
                        key={token.uid}
                        data-testid={token.is_current ? 'session-card-current' : 'session-card'}
                        className={`hover:shadow-md transition-shadow${token.is_current ? ' border-primary/40 ring-1 ring-primary/20' : ''}`}
                      >
                        <CardContent className="p-4 sm:p-6">
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                            <div className="flex items-start gap-4 min-w-0">
                              <div className="flex-shrink-0 mt-1">
                                {getDeviceIcon(deviceInfo, userAgent)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                  <h3 className="text-lg font-medium text-foreground">
                                    {deviceInfo.browser && deviceInfo.browser !== 'unknown'
                                      ? `${deviceInfo.browser}${deviceInfo.browser_version ? ` ${deviceInfo.browser_version}` : ''}`
                                      : 'Unknown Browser'}
                                  </h3>
                                  {token.is_current && (
                                    <span className="text-sm text-muted-foreground" data-testid="session-this-device">
                                      ({t('sessions.this_device')})
                                    </span>
                                  )}
                                  <Badge variant={tokenTypeInfo.variant}>
                                    {tokenTypeInfo.label}
                                  </Badge>
                                  {token.is_current && (
                                    <Badge variant="default" data-testid="session-current-badge">
                                      {t('sessions.current_badge')}
                                    </Badge>
                                  )}
                                  {createdWith?.mfa && (
                                    <Badge variant="outline" data-testid="session-mfa-badge">
                                      {t('sessions.mfa_badge')}
                                    </Badge>
                                  )}
                                </div>

                                <div className="space-y-2 text-sm text-muted-foreground">
                                  {((deviceInfo.os && deviceInfo.os !== 'unknown') || (deviceInfo?.device_type && deviceInfo.device_type !== 'unknown')) && (
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                      {deviceInfo.os && deviceInfo.os !== 'unknown' && (
                                        <div className="flex items-center gap-2">
                                          <AppIcon path={mdiLaptop} size="sm" />
                                          <span>{deviceInfo.os}{deviceInfo.os_version ? ` ${deviceInfo.os_version}` : ''}</span>
                                        </div>
                                      )}
                                      {deviceInfo?.device_type && deviceInfo.device_type !== 'unknown' && (
                                        <div className="flex items-center gap-2">
                                          <AppIcon path={mdiTablet} size="sm" />
                                          <span>{deviceInfo.device_type}</span>
                                        </div>
                                      )}
                                    </div>
                                  )}

                                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                    <div className="flex items-center gap-2">
                                      <AppIcon path={mdiCalendar} size="sm" />
                                      <span>{t('sessions.connected', { date: formatDate(token.created_at) })}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <AppIcon path={mdiClockOutline} size="sm" />
                                      <span>{t('sessions.last_active', { date: formatDate(token.last_used_at || null) })}</span>
                                    </div>
                                  </div>

                                  {userAgent && (
                                    <div className="mt-2 pt-2 border-t border-border/30">
                                      <div className="text-xs text-muted-foreground/60 font-mono break-all">
                                        {userAgent}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                            <SessionNetwork ip={ip} location={location} provider={provider} />
                          </div>
                        </CardContent>
                      </Card>
                    )
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
  )
}